import { useEffect, useId, useRef, useState } from 'react';
import { Check, Languages, Monitor, Moon, Sun } from 'lucide-react';
import { getTheme, useTheme, type ThemePref } from '../ui/theme.ts';
import { PALETTE_IDS, PALETTES, type PaletteId } from '../ui/palettes.ts';
import { getLang, LANGS, useLang, type Lang } from '../i18n/i18n.ts';

const MODE_OPTIONS: Array<{ value: ThemePref; sv: string; en: string; icon: typeof Sun }> = [
  { value: 'light', sv: 'Ljust', en: 'Light', icon: Sun },
  { value: 'dark', sv: 'Mörkt', en: 'Dark', icon: Moon },
  { value: 'system', sv: 'System', en: 'System', icon: Monitor },
];

/**
 * Ljust / Mörkt / System som en radiogrupp med riktiga radioknappar: Tab når gruppen, piltangenterna byter
 * val, skärmläsare läser "Tema, Ljust, alternativ 1 av 3". Valet gäller bara den här installationen.
 */
export function ThemeSwitch({ size = 'normal', legend, hideLegend = false }: { size?: 'normal' | 'compact'; legend?: string; hideLegend?: boolean }) {
  const { pref } = useTheme();
  const { t } = useLang();
  const name = useId();
  const compact = size === 'compact';
  return (
    <fieldset className="min-w-0">
      <legend className={hideLegend ? 'sr-only' : 'mb-1.5 text-sm font-medium'}>{legend ?? t('Tema', 'Theme')}</legend>
      <div className="autoday-theme-switch grid grid-cols-3 gap-1 rounded-lg border border-border bg-bg p-1">
        {MODE_OPTIONS.map(({ value, sv, en, icon: Icon }) => (
          <label key={value} className={`autoday-theme-option ${compact ? 'autoday-theme-option-compact min-h-11 text-xs' : 'min-h-11 text-sm'}`}>
            <input type="radio" className="sr-only" name={name} value={value} checked={pref === value} onChange={() => getTheme().choose(value)} />
            <Icon size={compact ? 14 : 16} aria-hidden />
            <span>{t(sv, en)}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** Litet prov av en palett i aktuellt läge: bakgrund, yta med text, accent och stödfärg. */
function Swatch({ id, mode, size = 'normal' }: { id: PaletteId; mode: 'light' | 'dark'; size?: 'normal' | 'mini' }) {
  const c = PALETTES[id][mode].swatch;
  if (size === 'mini') {
    return (
      <span aria-hidden className="relative block h-7 w-7 shrink-0 overflow-hidden rounded-full border border-border-strong" style={{ background: c.bg }}>
        <span className="absolute inset-y-0 right-0 w-1/2" style={{ background: c.accent }} />
        <span className="absolute bottom-0 left-0 h-1/2 w-1/2" style={{ background: c.support }} />
      </span>
    );
  }
  return (
    <span aria-hidden className="flex h-12 w-full overflow-hidden rounded-md border border-border" style={{ background: c.bg }}>
      <span className="m-1.5 flex flex-1 flex-col justify-center gap-1 rounded px-2" style={{ background: c.panel }}>
        <span className="block h-1.5 w-3/4 rounded-full" style={{ background: c.text }} />
        <span className="block h-1.5 w-1/2 rounded-full opacity-60" style={{ background: c.text }} />
      </span>
      <span className="my-1.5 mr-1.5 flex w-7 flex-col gap-1">
        <span className="block flex-1 rounded" style={{ background: c.accent }} />
        <span className="block h-2.5 rounded" style={{ background: c.support }} />
      </span>
    </span>
  );
}

/**
 * Färgpalett: Gryning / Norrsken / Original som radioknappar med namn och prov i aktuellt ljusläge.
 * compact = tre små prov (sidomenyn), annars kort med namn och beskrivning (Inställningar → Utseende).
 */
export function PaletteSwitch({ size = 'normal', hideLegend = false }: { size?: 'normal' | 'compact'; hideLegend?: boolean }) {
  const { palette, resolved } = useTheme();
  const { t, lang } = useLang();
  const name = useId();
  const compact = size === 'compact';
  return (
    <fieldset className="min-w-0">
      <legend className={hideLegend ? 'sr-only' : 'mb-1.5 text-sm font-medium'}>{t('Färgpalett', 'Colour palette')}</legend>
      <div className={compact ? 'flex items-center justify-between gap-1' : 'grid grid-cols-1 gap-2 sm:grid-cols-3'}>
        {PALETTE_IDS.map((id) => {
          const p = PALETTES[id];
          const checked = palette === id;
          return (
            <label
              key={id}
              title={compact ? p.name[lang] : undefined}
              className={
                compact
                  ? `autoday-palette-option flex min-h-11 flex-1 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-lg text-[11px] ${checked ? 'font-semibold text-text' : 'text-muted hover:bg-raised'}`
                  : `autoday-palette-option flex min-h-11 cursor-pointer flex-col gap-2 rounded-xl border p-2.5 text-sm ${checked ? 'border-accent bg-accent-soft' : 'border-border hover:border-border-strong'}`
              }
            >
              <input type="radio" className="sr-only" name={name} value={id} checked={checked} onChange={() => getTheme().choosePalette(id)} />
              {compact ? (
                <>
                  <span className={`rounded-full p-0.5 ${checked ? 'ring-2 ring-accent' : ''}`}>
                    <Swatch id={id} mode={resolved} size="mini" />
                  </span>
                  <span className="max-w-full truncate">{p.name[lang]}</span>
                </>
              ) : (
                <>
                  <Swatch id={id} mode={resolved} />
                  <span className="flex items-center gap-1.5 font-semibold">
                    {checked && <Check size={16} className="shrink-0 text-accent" aria-hidden />}
                    <span>{p.name[lang]}</span>
                  </span>
                  <span className="text-xs text-muted">{p.description[lang]}</span>
                </>
              )}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * Språk: Svenska / English med egna språknamn (inga flaggor) och en språkikon. Byte gäller direkt och
 * sparas på enheten. Ändrar aldrig tidszon, tider, larm eller användarens egen text.
 */
export function LanguageSwitch({ size = 'normal', hideLegend = false, autoFocus = false }: { size?: 'normal' | 'compact'; hideLegend?: boolean; autoFocus?: boolean }) {
  const { lang } = useLang();
  const name = useId();
  const compact = size === 'compact';
  return (
    <fieldset className="min-w-0" lang={lang}>
      <legend className={hideLegend ? 'sr-only' : 'mb-1.5 flex items-center gap-1.5 text-sm font-medium'}>
        <Languages size={16} aria-hidden className={hideLegend ? 'hidden' : 'shrink-0'} />
        {/* Båda språken i rubriken: den som inte läser svenska hittar ändå rätt. */}
        Språk · Language
      </legend>
      <div className="autoday-theme-switch grid grid-cols-2 gap-1 rounded-lg border border-border bg-bg p-1">
        {LANGS.map(({ value, label }) => (
          <label key={value} lang={value} className={`autoday-theme-option ${compact ? 'min-h-11 text-xs' : 'min-h-11 text-sm'}`}>
            <input type="radio" className="sr-only" name={name} value={value} checked={lang === value} autoFocus={autoFocus && lang === value} onChange={() => getLang().choose(value as Lang)} />
            {compact && <Languages size={14} aria-hidden />}
            <span>{label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** Mobilhuvudet: en ikonknapp som visar aktivt tema och öppnar utseende och språk. Esc och klick utanför stänger. */
export function ThemeHeaderButton() {
  const { pref, resolved, palette } = useTheme();
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const btn = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const Icon = pref === 'system' ? Monitor : resolved === 'light' ? Sun : Moon;
  const mode = MODE_OPTIONS.find((o) => o.value === pref)!;
  const current = `${t(mode.sv, mode.en)}, ${PALETTES[palette].name[lang]}`;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btn.current?.focus();
      }
    };
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!panel.current?.contains(target) && !btn.current?.contains(target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    panel.current?.querySelector<HTMLInputElement>('input:checked')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);

  return (
    // Menyn placeras mot sidhuvudets högra kant (sidhuvudet är positionerat) så att den aldrig hamnar utanför skärmen.
    <div>
      <button
        ref={btn}
        type="button"
        className="grid min-h-11 min-w-11 place-items-center rounded-lg text-muted hover:bg-raised hover:text-text"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`${t('Tema', 'Theme')}: ${current}`}
        title={`${t('Tema', 'Theme')}: ${current}`}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon size={20} aria-hidden />
      </button>
      {open && (
        <div ref={panel} id={panelId} className="absolute right-3 top-full z-30 mt-1 grid max-h-[calc(100dvh-5rem)] w-[min(19rem,calc(100vw-1.5rem))] gap-3 overflow-y-auto rounded-xl border border-border-strong bg-panel p-3 shadow-xl">
          <ThemeSwitch />
          <PaletteSwitch size="compact" />
          <LanguageSwitch size="compact" />
          <p className="text-xs text-muted">{t('Sparas bara på den här enheten.', 'Saved on this device only.')}</p>
        </div>
      )}
    </div>
  );
}


